import type { UserProfile } from "@serophin/nottldr-shared";
import { useProfile } from "@serophin/nottldr-shared/hooks";
import { Loader2 } from "lucide-preact";
import { ProfileTabs } from "../../components/profile/ProfileTabs";
import { AccountSettings } from "../../components/settings/AccountSettings";
import { AppearanceSettings } from "../../components/settings/AppearanceSettings";
import { EmailPreferences } from "../../components/settings/EmailPreferences";
import { NarratorSettings } from "../../components/settings/NarratorSettings";
import { ReadingSettings } from "../../components/settings/ReadingSettings";
import { SettingsMenu } from "../../components/settings/SettingsMenu";
import { SettingsPanel } from "../../components/settings/SettingsPanel";
import { type SettingsState, useSettingsState } from "../../components/settings/useSettingsState";
import { useIsSignedIn } from "../../platform/session";
import type { PageProps } from "../pageProps";
import { useSettingsTab } from "./useSettingsTab";

const Loading = () => (
  <div class="flex justify-center items-center h-full py-12">
    <Loader2 size={36} class="animate-spin mr-2 text-gray-500" />
    <div class="text-lg text-gray-500">Loading your preferences...</div>
  </div>
);

type PanelsProps = { tab: string | null; close: () => void; signedIn: boolean; settings: SettingsState; profile: UserProfile | null };

const Panels = ({ tab, close, signedIn, settings, profile }: PanelsProps) => (
  <>
    <SettingsPanel isOpen={tab === "narrator"} onClose={close}>
      <NarratorSettings settings={settings} />
    </SettingsPanel>
    <SettingsPanel isOpen={tab === "reading"} onClose={close}>
      <ReadingSettings settings={settings} />
    </SettingsPanel>
    <SettingsPanel isOpen={tab === "appearance"} onClose={close}>
      <AppearanceSettings />
    </SettingsPanel>
    {signedIn && (
      <SettingsPanel isOpen={tab === "email"} onClose={close}>
        <EmailPreferences settings={settings} />
      </SettingsPanel>
    )}
    {signedIn && profile && (
      <SettingsPanel isOpen={tab === "account"} onClose={close}>
        <AccountSettings profile={profile} />
      </SettingsPanel>
    )}
  </>
);

const MENU_HIDDEN = "-translate-x-full opacity-0 pointer-events-none absolute";

const SettingsContent = ({ signedIn }: { signedIn: boolean }) => {
  const { tab, open } = useSettingsTab();
  const settings = useSettingsState();
  const { profile } = useProfile();

  return (
    <div class="max-w-2xl mx-auto overflow-hidden relative">
      <div class={`transition-all ease-in-out ${tab ? MENU_HIDDEN : "translate-x-0 opacity-100"}`}>
        <SettingsMenu settings={settings} email={profile?.email} onTabChange={open} />
      </div>
      <Panels tab={tab} close={() => open(null)} signedIn={signedIn} settings={settings} profile={profile} />
    </div>
  );
};

/** `/profile/settings`. It works signed out too, with the settings kept in this browser. */
export const SettingsPage = (_props: PageProps) => {
  const signedIn = useIsSignedIn();
  const { profile, isLoading } = useProfile();
  return (
    <>
      <ProfileTabs active="settings" />
      {signedIn && isLoading && !profile ? <Loading /> : <SettingsContent signedIn={signedIn} />}
    </>
  );
};
